/**
 * CertificateVerifyPage — Public verification page for /verify/{cert_id}.
 *
 * No login required. Confirms whether a certificate is real, showing only:
 *   - First name (no last name, no email)
 *   - Business name (if set)
 *   - Completion %
 *   - Issue date
 *
 * Designed to be linked from the QR code printed on the certificate.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, AlertCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CertificateVerifyPage() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/certificate/verify/${certId}`);
        if (!cancelled) setState({ loading: false, data: res.data, error: null });
      } catch (err) {
        if (!cancelled) {
          setState({
            loading: false,
            data: null,
            error: err?.response?.status === 404
              ? "We couldn't find this certificate."
              : "Something went wrong while verifying.",
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [certId]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center p-6">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" /> Verifying certificate…
        </div>
      </div>
    );
  }

  if (state.error || !state.data?.valid) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-amber-200 shadow-lg p-8 max-w-md text-center" data-testid="verify-not-found">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" aria-hidden="true" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Certificate not verified</h1>
          <p className="text-sm text-gray-600 mb-4">{state.error || "This ID did not match any certificate on file."}</p>
          <p className="text-xs text-gray-400">
            ID checked: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{certId}</code>
          </p>
          <Link to="/" className="mt-6 inline-block text-sm text-amber-600 hover:underline">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const d = state.data;
  return (
    <div className="min-h-screen bg-amber-50 flex items-center justify-center p-6">
      <div
        className="bg-white rounded-2xl border-t-4 border-amber-500 shadow-xl p-8 max-w-lg w-full text-center"
        data-testid="verify-success"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 mb-4">
          <ShieldCheck className="w-8 h-8 text-emerald-600" aria-hidden="true" />
        </div>
        <p className="text-xs font-bold text-emerald-700 uppercase tracking-widest mb-1">Verified</p>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          {d.first_name} earned the
        </h1>
        <p className="text-base text-gray-700 mb-5">
          Lemonade Stand Business Plan · Youth Entrepreneurship Certificate
        </p>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm space-y-2 text-left">
          <div className="flex justify-between">
            <span className="text-gray-500">Certificate ID</span>
            <span className="font-mono font-bold text-gray-900">{d.certificate_id}</span>
          </div>
          {d.business_name && (
            <div className="flex justify-between">
              <span className="text-gray-500">Business name</span>
              <span className="font-semibold text-gray-900">{d.business_name}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-500">Program completion</span>
            <span className="font-semibold text-gray-900">{d.completion_percentage}%</span>
          </div>
          {d.issued_at && (
            <div className="flex justify-between">
              <span className="text-gray-500">Issued</span>
              <span className="text-gray-900">
                {new Date(d.issued_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-6">
          Issued by The WE Mentality · Lemonade Stand Business Plan
        </p>
      </div>
    </div>
  );
}
